import React, { useState, useEffect, useCallback } from 'react';
import { shotListAPI } from '../utils/api';
import { Camera, ChevronDown, ChevronUp, CheckCircle2, Image as ImageIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'must_have', label: 'Must Have' },
  { key: 'getting_ready', label: 'Getting Ready' },
  { key: 'ceremony', label: 'Ceremony' },
  { key: 'family', label: 'Family' },
  { key: 'couple', label: 'Couple' },
  { key: 'details', label: 'Details' },
  { key: 'reception', label: 'Reception' },
  { key: 'candid', label: 'Candid' },
  { key: 'other', label: 'Other' },
];

const ShotListCrew = ({ eventId }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await shotListAPI.get(eventId);
      setList(r.data);
    } catch (e) {
      console.error('shot list fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (open && !list) fetchList();
  }, [open, list, fetchList]);

  const toggle = async (item) => {
    try {
      await shotListAPI.updateItem(item.item_id, { captured: !item.captured });
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
  };

  const stats = list?.stats || { total: 0, captured: 0, percent: 0 };

  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid={`crew-shotlist-${eventId}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between w-full text-left"
        data-testid={`crew-shotlist-toggle-${eventId}`}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <Camera size={14} className="text-gold" />
          Shot List
          {list && (
            <span className="text-xs text-muted-foreground font-normal">
              {stats.captured}/{stats.total} captured · {stats.percent}%
            </span>
          )}
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="mt-3">
          {loading && !list ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : !list || stats.total === 0 ? (
            <p className="text-xs text-muted-foreground italic">No shot list set up for this event yet.</p>
          ) : (
            <div className="space-y-3">
              {CATEGORIES.map(({ key, label }) => {
                const items = list.by_category?.[key] || [];
                if (items.length === 0) return null;
                return (
                  <div key={key}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</p>
                    <div className="space-y-1.5">
                      {items.map(item => (
                        <div
                          key={item.item_id}
                          className={`flex items-start gap-2 p-2 border ${item.captured ? 'border-green-200 dark:border-green-500/30 bg-green-50/40' : 'border-border'}`}
                          data-testid={`crew-shot-${item.item_id}`}
                        >
                          <button
                            onClick={() => toggle(item)}
                            className={`flex-shrink-0 w-5 h-5 mt-0.5 border-2 flex items-center justify-center ${
                              item.captured ? 'bg-green-600 border-green-600' : 'border-border hover:border-gold'
                            }`}
                            data-testid={`crew-shot-toggle-${item.item_id}`}
                            aria-label={item.captured ? 'Unmark' : 'Mark captured'}
                          >
                            {item.captured && <CheckCircle2 size={12} className="text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${item.captured ? 'line-through text-muted-foreground' : 'font-medium'}`}>
                              {item.title}
                              {item.source === 'client' && (
                                <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-pink-600 dark:text-pink-400">
                                  <Sparkles size={10} /> couple
                                </span>
                              )}
                            </p>
                            {item.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                            )}
                          </div>
                          {item.reference_image_path && (
                            <span className="inline-flex items-center text-muted-foreground" title="Reference photo attached">
                              <ImageIcon size={14} />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShotListCrew;
